import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { DateTime } from 'luxon'
import SimpleTable from '@modul-connect/shared/components/molecules/tableWidget/simpleTable'
import { groupingOptions } from './temperatureHistory'
import { Link } from '@modul-connect/shared/components/atoms/text/text'
import { useTranslation } from 'react-i18next'

const formatEmails = (emails) => 
  emails === null ? '' : emails.map(e => {
    const time = DateTime
      .fromISO(e.sentAt, { zone: 'utc' })
      .toLocal()
      .toFormat('HH:mm')
    return `${time}, ${e.email}\n`
  })

const formatTemperatures = (minTemp, maxTemp) =>
  `${minTemp !== null ? minTemp.toFixed(1) : '-'}–${maxTemp !== null ? maxTemp.toFixed(1) : '-'}°C`

const formatVehicleIncidents = (incidents) => {

  const formattedList = []

  const sortedIncidents = [...incidents].sort((a, b) => {
    return (
      new Date(a.createdAt).getTime() -
      new Date(b.createdAt).getTime()
    )
  })

  sortedIncidents.forEach((incident) => {
    if (incident.latestIncidentEndedAt && new Date(incident.latestIncidentEndedAt) < new Date(incident.createdAt)) {
      const fillerBefore = {
        createdAt: incident.latestIncidentEndedAt,
        endedAt: incident.createdAt,
        severity: 'None',
        lowestTemperature: incident.lowestTemperatureSinceLatestIncident,
        highestTemperature: incident.highestTemperatureSinceLatestIncident,
        isIncident: false,
        sentEmails: []
      }
      formattedList.push(fillerBefore)
    }

    const transformedIncident = {
      createdAt: incident.createdAt,
      endedAt: incident.endedAt,
      severity: incident.severity,
      lowestTemperature: incident.lowestTemperature,
      highestTemperature: incident.highestTemperature,
      isIncident: true,
      sentEmails: incident.sentEmails
    }
    formattedList.push(transformedIncident)

    if (incident.lastIncident && incident.endedAt) {
      const fillerAfter = {
        createdAt: incident.endedAt,
        endedAt: incident.nextIncidentDate,
        severity: 'None',
        lowestTemperature: incident.lowestTemperatureToEnd,
        highestTemperature: incident.highestTemperatureToEnd,
        isIncident: false,
        sentEmails: []
      }
      formattedList.push(fillerAfter)
    }
  })

  return formattedList
}

export const IncidentDetailsTable = ({ mainboxId, columns, showSelection, setOpenModal, setSelectedMainboxId, setSelectedStartDate, setSelectedEndDate }) => {

  const loadingState = useSelector(state => state.loading.allClimateControlIncidents)
  const cachedData = useSelector(state => state.climateControlIncidents.incidents?.[mainboxId])

  const { t } = useTranslation()

  const isLoading = loadingState?.[mainboxId] === 'loading'

  const tableData = useMemo(() => {

    const vehicleIncidents = (isLoading || !cachedData) ? [] : cachedData

    if (!vehicleIncidents?.length) return []

    const formattedVehicleIncidents = formatVehicleIncidents(vehicleIncidents)

    const incidentRows = formattedVehicleIncidents.map(i => {
      const endedDate = i.endedAt && DateTime.fromISO(i.endedAt)
      const createdDate = i.createdAt && DateTime.fromISO(i.createdAt)
      const endDateStr = endedDate ? endedDate.toFormat('yyyy-MM-dd') : t('climateSystem.tempHistory.ongoing')
      const startDateStr = createdDate ? createdDate.toFormat('yyyy-MM-dd') : '-'
      const endTimeStr = endedDate ? endedDate.toFormat('HH:mm') : t('climateSystem.tempHistory.ongoing')
      const startTimeStr = createdDate ? createdDate.toFormat('HH:mm') : '-'
      let durationStr = t('climateSystem.tempHistory.ongoing')
      if (endedDate && createdDate) {
        const duration = endedDate
          ? endedDate.diff(createdDate, ['hours', 'minutes'])
          : DateTime.now().diff(createdDate, ['hours', 'minutes'])
        durationStr = duration.toFormat('h\'h\' m\'m\'') 
      }
      
      return {
        dateEnd: endDateStr,
        endedDate: endedDate,
        dateStart: startDateStr,
        createdDate: createdDate,
        timeEnd: endTimeStr,
        timeStart: startTimeStr,
        duration: durationStr,
        isIncident: i.isIncident,
        temperature: formatTemperatures(i.lowestTemperature, i.highestTemperature),
        sentEmails: formatEmails(i.sentEmails)
      }
    })
      .filter(r => showSelection === groupingOptions.onlyWarning ? r.isIncident : r)
      .sort((a, b) => {
        if (!a.createdDate) return 1
        if (!b.createdDate) return -1
        return b.createdDate.toMillis() - a.createdDate.toMillis()
      })

    const finalRows = []
    incidentRows.forEach((row) => {
      finalRows.push([
        { value1: t('climateSystem.tempReport.to') + ': ' + row.dateEnd, value2: t('climateSystem.tempReport.from') + ': ' + row.dateStart },
        { value1: row.timeEnd, value2: row.timeStart },
        { value: row.duration },
        { value: row.temperature, warning: row.isIncident },
        { value: row.sentEmails},
        { value: <Link onClick={() => {
          setSelectedMainboxId(mainboxId)
          setSelectedStartDate(row.createdDate)
          if (row.endedDate)
            setSelectedEndDate(row.endedDate)
          else
            setSelectedEndDate(DateTime.now())
          setOpenModal(true)
        }}>
          {t('climateSystem.tempHistory.chart')}
        </Link> }
      ]) 
    })

    return finalRows
  }, [isLoading, cachedData, t, showSelection, setSelectedMainboxId, mainboxId, setSelectedStartDate, setSelectedEndDate, setOpenModal])

  return (
    <SimpleTable
      data={tableData}
      columns={columns}
      showSortArrows
      isLoading={isLoading}
      defaultSortIndex={0}
      defaultSortOrder={'desc'}
    />
  )
}
